import { useState, useCallback, useEffect, useMemo } from 'react';
import { Stack, Group, Text, Button, Card, Box, Select, Modal } from '@mantine/core';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import {
  DndContext,
  closestCenter,
  type DragEndEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  arrayMove,
} from '@dnd-kit/sortable';
import { IconPlus, IconListTree } from '@tabler/icons-react';
import { useAddChild, useRemoveChild, useSaveChildOrder } from '../useAppContentMutations';
import { SortableChildItem } from './SortableChildItem';

interface OrderedSeasonsEditorProps {
  parentLlId: number;
  orderedListIds: number[];
  playlistLookup: Map<number, { listId: number; title: string }>;
}

export function OrderedSeasonsEditor({
  parentLlId,
  orderedListIds,
  playlistLookup,
}: OrderedSeasonsEditorProps) {
  const addMutation = useAddChild();
  const removeMutation = useRemoveChild();
  const saveOrderMutation = useSaveChildOrder();

  // Local draft state for ordering
  const [draftOrder, setDraftOrder] = useState<number[]>(orderedListIds);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [selectedListId, setSelectedListId] = useState<string | null>(null);

  // Sync draft when server data changes
  useEffect(() => {
    setDraftOrder(orderedListIds);
  }, [orderedListIds]);

  const hasOrderChanges = useMemo(() => {
    if (draftOrder.length !== orderedListIds.length) return false;
    return draftOrder.some((id, i) => id !== orderedListIds[i]);
  }, [draftOrder, orderedListIds]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;

      const oldIndex = draftOrder.indexOf(active.id as number);
      const newIndex = draftOrder.indexOf(over.id as number);

      if (oldIndex !== -1 && newIndex !== -1) {
        setDraftOrder(arrayMove(draftOrder, oldIndex, newIndex));
      }
    },
    [draftOrder],
  );

  // Available playlists (not already assigned)
  const addOptions = useMemo(() => {
    const existingSet = new Set(draftOrder);
    return Array.from(playlistLookup.values())
      .filter((p) => !existingSet.has(p.listId))
      .sort((a, b) => a.title.localeCompare(b.title))
      .map((p) => ({
        value: String(p.listId),
        label: p.title,
      }));
  }, [playlistLookup, draftOrder]);

  const handleAdd = useCallback(() => {
    if (!selectedListId) return;
    const listId = parseInt(selectedListId, 10);
    addMutation.mutate(
      {
        parentLLId: parentLlId,
        childId: { childLLId: null, childListId: listId },
      },
      {
        onSuccess: () => {
          setAddDialogOpen(false);
          setSelectedListId(null);
          notifications.show({
            title: 'Added',
            message: 'Season added successfully.',
            color: 'green',
          });
        },
        onError: (err) => {
          notifications.show({ title: 'Error', message: err.message, color: 'red' });
        },
      },
    );
  }, [parentLlId, selectedListId, addMutation]);

  const handleRemove = useCallback(
    (listId: number) => {
      const playlist = playlistLookup.get(listId);
      const title = playlist?.title ?? 'this season';

      modals.openConfirmModal({
        title: 'Remove Season',
        children: (
          <Text size="sm">
            Remove &ldquo;{title}&rdquo; from this series? The playlist itself
            will not be deleted.
          </Text>
        ),
        labels: { confirm: 'Remove', cancel: 'Cancel' },
        confirmProps: { color: 'red' },
        onConfirm: () => {
          removeMutation.mutate(
            {
              parentLLId: parentLlId,
              childId: { childLLId: null, childListId: listId },
            },
            {
              onSuccess: () => {
                setDraftOrder((prev) => prev.filter((id) => id !== listId));
                notifications.show({
                  title: 'Removed',
                  message: 'Season removed.',
                  color: 'green',
                });
              },
              onError: (err) => {
                notifications.show({ title: 'Error', message: err.message, color: 'red' });
              },
            },
          );
        },
      });
    },
    [parentLlId, playlistLookup, removeMutation],
  );

  const handleSaveOrder = useCallback(() => {
    saveOrderMutation.mutate(
      {
        parentLLId: parentLlId,
        childIds: draftOrder.map((listId) => ({ childLLId: null, childListId: listId })),
      },
      {
        onSuccess: () => {
          notifications.show({
            title: 'Saved',
            message: 'Seasons order saved.',
            color: 'green',
          });
        },
        onError: (err) => {
          notifications.show({ title: 'Error', message: err.message, color: 'red' });
        },
      },
    );
  }, [parentLlId, draftOrder, saveOrderMutation]);

  const handleDiscardOrder = useCallback(() => {
    setDraftOrder(orderedListIds);
  }, [orderedListIds]);

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <Text fw={600} size="lg">
          Seasons ({draftOrder.length})
        </Text>
        <Group gap="sm">
          {hasOrderChanges && (
            <>
              <Button variant="default" size="sm" onClick={handleDiscardOrder}>
                Discard Order
              </Button>
              <Button
                size="sm"
                onClick={handleSaveOrder}
                loading={saveOrderMutation.isPending}
              >
                Save Order
              </Button>
            </>
          )}
          <Button
            leftSection={<IconPlus size={16} />}
            variant="light"
            size="sm"
            onClick={() => setAddDialogOpen(true)}
          >
            Add Season
          </Button>
        </Group>
      </Group>

      {draftOrder.length === 0 ? (
        <Card withBorder p="xl">
          <Stack align="center" gap="sm">
            <IconListTree size={36} stroke={1.5} opacity={0.4} />
            <Text size="sm" c="dimmed" ta="center">
              No seasons assigned yet. Add a playlist as a season.
            </Text>
            <Button
              variant="light"
              leftSection={<IconPlus size={14} />}
              size="sm"
              onClick={() => setAddDialogOpen(true)}
            >
              Add Season
            </Button>
          </Stack>
        </Card>
      ) : (
        <Card withBorder p="sm">
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={draftOrder}
              strategy={verticalListSortingStrategy}
            >
              <Stack gap="xs">
                {draftOrder.map((listId, index) => {
                  const playlist = playlistLookup.get(listId);
                  return (
                    <SortableChildItem
                      key={listId}
                      id={listId}
                      index={index}
                      title={playlist?.title ?? `Playlist ${listId}`}
                      onRemove={handleRemove}
                    />
                  );
                })}
              </Stack>
            </SortableContext>
          </DndContext>
          <Text size="xs" c="dimmed" mt="xs" ta="center">
            Drag to reorder
          </Text>
        </Card>
      )}

      {/* Add Season Dialog */}
      <Modal
        opened={addDialogOpen}
        onClose={() => {
          setAddDialogOpen(false);
          setSelectedListId(null);
        }}
        title="Add Season"
        size="md"
      >
        <Stack gap="md">
          {addOptions.length === 0 ? (
            <Text size="sm" c="dimmed">
              No available playlists to add. Create a playlist first.
            </Text>
          ) : (
            <Select
              label="Select Playlist"
              placeholder="Choose a playlist"
              data={addOptions}
              value={selectedListId}
              onChange={setSelectedListId}
              searchable
              data-autofocus
            />
          )}

          <Group justify="flex-end">
            <Button
              variant="default"
              onClick={() => {
                setAddDialogOpen(false);
                setSelectedListId(null);
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleAdd}
              disabled={!selectedListId || addOptions.length === 0}
              loading={addMutation.isPending}
            >
              Add Season
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}
